import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AVATARS, randomAvatar } from "@/lib/avatars";
import { Avatar, PaperButton } from "@/components/paper";
import { TopNav } from "@/pages/Home";
import { api, errMsg } from "@/lib/api";
import { audio } from "@/lib/audio";
import { toast } from "sonner";
import { Shuffle, Dice5 } from "lucide-react";

export default function PlaySetup() {
  const nav = useNavigate();
  const params = useParams();
  const [sp] = useSearchParams();
  const { playerId, username, setUsername, avatar, setAvatar } = useApp();
  const mode = params.code ? "join" : (sp.get("mode") || "create");
  const [name, setName] = useState(username || "");
  const [code, setCode] = useState((params.code || "").toUpperCase());
  const [cat, setCat] = useState("All");
  const [busy, setBusy] = useState(false);

  const cats = useMemo(() => ["All", ...Array.from(new Set(AVATARS.map((a) => a.cat)))], []);
  const shown = cat === "All" ? AVATARS : AVATARS.filter((a) => a.cat === cat);

  const cleanName = (v) => v.replace(/[<>]/g, "").slice(0, 16);

  const proceed = async () => {
    const n = cleanName(name).trim();
    if (n.length < 2) { toast.error("Username must be 2–16 characters."); audio.play("error"); return; }
    setUsername(n);
    setBusy(true);
    try {
      if (mode === "create") {
        const { data } = await api.post("/rooms/create", { playerId });
        audio.play("createRoom");
        nav(`/room/${data.code}`);
      } else {
        const c = code.trim().toUpperCase();
        if (c.length < 4) { toast.error("Enter a valid room code."); audio.play("error"); setBusy(false); return; }
        const { data } = await api.get(`/rooms/${c}/check`, { params: { playerId } });
        if (!data.ok) { toast.error(data.reason || "Room not found."); audio.play("error"); setBusy(false); return; }
        audio.play("joinRoom");
        nav(`/room/${c}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
      audio.play("error");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen">
      <TopNav nav={nav} />
      <div className="max-w-4xl mx-auto px-5 py-8">
        <h1 className="font-hand font-bold text-4xl mb-1">{mode === "create" ? "Create a Room" : "Join a Room"}</h1>
        <p className="font-semibold text-ink2 mb-6">Pick a name and an avatar — no account needed.</p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-paper p-6">
            <label className="font-hand font-bold text-lg">Username</label>
            <input
              data-testid="username-input"
              value={name}
              maxLength={16}
              onChange={(e) => setName(cleanName(e.target.value))}
              placeholder="e.g. Ravi"
              className="w-full mt-2 px-4 py-3 card-flat font-semibold text-lg focus-mark outline-none"
            />
            <div className="text-xs font-semibold text-ink2 mt-1">{name.length}/16 characters</div>

            {mode === "join" && (
              <div className="mt-5">
                <label className="font-hand font-bold text-lg">Room Code</label>
                <input
                  data-testid="room-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                  placeholder="K7P4X"
                  className="w-full mt-2 px-4 py-3 card-flat font-hand font-bold text-2xl tracking-[0.3em] text-center focus-mark outline-none"
                />
              </div>
            )}

            <div className="mt-6 flex items-center gap-3">
              <Avatar seed={avatar} size={64} />
              <div>
                <div className="font-hand font-bold text-xl">{name || "Your name"}</div>
                <button data-testid="random-avatar" onClick={() => { setAvatar(randomAvatar()); audio.play("click"); }} className="inline-flex items-center gap-1.5 font-semibold text-marker-blue hover:underline">
                  <Dice5 size={16} strokeWidth={2.5} /> Random avatar
                </button>
              </div>
            </div>

            <PaperButton
              data-testid="setup-confirm"
              disabled={busy}
              color="#FFDD2D"
              className="w-full mt-6 text-xl"
              onClick={proceed}
            >{busy ? "Please wait…" : (mode === "create" ? "CREATE ROOM" : "JOIN ROOM")}</PaperButton>
          </div>

          <div className="card-paper p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-hand font-bold text-lg">Choose Avatar</div>
              <button onClick={() => { setAvatar(randomAvatar()); audio.play("click"); }} className="btn-sketch px-3 py-1.5 text-sm"><Shuffle size={14} strokeWidth={2.5} className="inline mr-1" />Random</button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {cats.map((c) => (
                <button key={c} data-testid={`avatar-cat-${c}`} onClick={() => { setCat(c); audio.play("hover"); }} className="font-hand font-bold text-sm px-2.5 py-1" style={{ borderRadius: 8, background: cat === c ? "#FFDD2D" : "#FDFBF7", border: "2px solid #1C1B1A" }}>{c}</button>
              ))}
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[320px] overflow-y-auto pr-1">
              {shown.map((a) => (
                <button key={a.id} data-testid={`avatar-${a.id}`} onClick={() => { setAvatar(a.id); audio.play("click"); }} className="p-1" style={{ borderRadius: 12, background: avatar === a.id ? "#FFDD2D" : "transparent" }}>
                  <Avatar seed={a.id} size={56} className="w-full h-auto" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
